import { ThemeSelector, DensitySelector, ContrastToggle } from '@insolitum/ui';
import {
  Settings,
  Building2,
  Palette,
  MapPin,
  Globe,
  Phone,
  Mail,
  Save,
  AlertCircle,
  Check,
  Trash2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { KartaAlarmowSms } from '../../components/ustawienia/KartaAlarmowSms';
import { KartaDwuskladnikowe } from '../../components/ustawienia/KartaDwuskladnikowe';
import { KartaPowiadomien } from '../../components/ustawienia/KartaPowiadomien';
import {
  listaStref,
  podgladCzasu,
  strefaPoprawna,
  strefaPrzegladarki,
  STREFA_DOMYSLNA,
} from '../../lib/strefyCzasowe';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

interface OrganizationDetails {
  id: string;
  name: string;
  slug: string;
  status: string;
  subscription_plan: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  nip?: string;
  latitude?: number;
  longitude?: number;
  settings?: Record<string, unknown>;
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { currentOrganization, hasRole, refreshUserData } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Strefa czasowa organizacji siedzi w `organizations.settings.timezone`, nie
  // w osobnej kolumnie — trzymamy więc CAŁY obiekt `settings`, żeby zapis strefy
  // nie skasował pozostałych kluczy (zapis to podmiana całego JSON-a).
  const [orgSettings, setOrgSettings] = useState<Record<string, unknown>>({});
  const [timezone, setTimezone] = useState<string>(STREFA_DOMYSLNA);
  const strefy = useMemo(() => listaStref(), []);
  const strefaLokalna = useMemo(() => strefaPrzegladarki(), []);

  const [formData, setFormData] = useState<Partial<OrganizationDetails>>({
    name: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    nip: '',
  });

  const canEditSettings = hasRole(['owner', 'admin']);

  useEffect(() => {
    if (currentOrganization) {
      fetchOrganizationDetails();
    }
  }, [currentOrganization]);

  const fetchOrganizationDetails = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', currentOrganization.organization_id)
        .single();

      if (error) throw error;

      setFormData({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || '',
        postal_code: data.postal_code || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || '',
        nip: data.nip || '',
      });

      const settings = (data.settings ?? {}) as Record<string, unknown>;
      setOrgSettings(settings);
      const zapisana = typeof settings.timezone === 'string' ? settings.timezone.trim() : '';
      setTimezone(zapisana && strefaPoprawna(zapisana) ? zapisana : STREFA_DOMYSLNA);
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError('Failed to load organization details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !canEditSettings) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: formData.name,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          postal_code: formData.postal_code,
          phone: formData.phone,
          email: formData.email,
          website: formData.website,
          nip: formData.nip,
          // Scalamy, a nie nadpisujemy: `settings` niesie też inne klucze
          // konfiguracji organizacji i zapis strefy nie ma prawa ich zgubić.
          settings: { ...orgSettings, timezone },
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentOrganization.organization_id);

      if (error) throw error;

      setOrgSettings((poprzednie) => ({ ...poprzednie, timezone }));
      setSuccess(t('settings.saved', 'Settings saved successfully'));
      await refreshUserData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-aurora-brand border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Settings className="w-6 h-6 text-aurora-brand" />
            {t('settings.title', 'Organization Settings')}
          </h1>
          <p className="pagehead__sub">
            {t('settings.subtitle', "Manage your organization's details and preferences")}
          </p>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/30 rounded-ids-md text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/20 border border-green-500/30 rounded-ids-md text-green-400">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Wygląd i dostępność — trzy osie Aurory. Tokeny na nie istniały od
          pierwszej fali (`data-mode` / `data-density` / `data-contrast`), ale nic
          ich nie ustawiało, więc tryb jasny, gęstości i wysoki kontrast były
          niedostępne dla użytkownika. Preferencja trzyma się w localStorage
          i jest nakładana przed pierwszym malowaniem (skrypt w index.html). */}
      <div className="card">
        <div className="card__head">
          <Palette className="w-5 h-5 text-aurora-brand" />
          <h2 className="card__title">{t('settings.appearance', 'Wygląd i dostępność')}</h2>
        </div>
        <div className="card__body space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{t('settings.themeMode', 'Motyw')}</p>
              <p className="text-sm text-aurora-muted">
                {t('settings.themeModeHint', 'Ciemny, jasny albo zgodnie z ustawieniem systemu.')}
              </p>
            </div>
            <ThemeSelector />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{t('settings.density', 'Gęstość interfejsu')}</p>
              <p className="text-sm text-aurora-muted">
                {t(
                  'settings.densityHint',
                  'Zwarta mieści więcej danych, kiosk powiększa cele dotyku do 64 px.'
                )}
              </p>
            </div>
            <DensitySelector />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{t('settings.contrast', 'Wysoki kontrast')}</p>
              <p className="text-sm text-aurora-muted">
                {t('settings.contrastHint', 'Mocniejsze obramowania i grubszy pierścień focusu.')}
              </p>
            </div>
            <ContrastToggle />
          </div>
        </div>
      </div>

      {/* 2FA — cecha konta zalogowanego, nie organizacji; ta sama karta
          w ustawieniach partnera i super-admina (components/ustawienia). */}
      <KartaDwuskladnikowe />

      {/* Powiadomienia e-mail — tryby i reguły per organ (migracja 889).
          `?organ=<organ>` rozwija sekcję jednego organu. */}
      <KartaPowiadomien />

      {/* Alarmy SMS — zgoda zalogowanej osoby i jej numer (users.phone). */}
      <KartaAlarmowSms />

      {/* Organization Status */}
      <div className="card">
        <div className="card__head">
          <Building2 className="w-5 h-5 text-aurora-brand" />
          <h2 className="card__title">{t('settings.status', 'Organization Status')}</h2>
        </div>
        <div className="card__body grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
            <p className="overline">{t('settings.status', 'Status')}</p>
            <p className="mt-1">
              <span
                className={`pill ${
                  currentOrganization?.organization.status === 'active'
                    ? 'pill--active'
                    : currentOrganization?.organization.status === 'trial'
                      ? 'pill--trial'
                      : 'pill--error'
                }`}
              >
                <span className="pin" />
                {t(`status.${currentOrganization?.organization.status ?? 'undefined'}`, {
                  defaultValue: currentOrganization?.organization.status ?? 'Unknown',
                })}
              </span>
            </p>
          </div>
          <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
            <p className="overline">{t('settings.plan', 'Plan')}</p>
            <p className="text-lg font-semibold text-aurora-text capitalize mt-1">
              {t(`plans.${currentOrganization?.organization.subscription_plan ?? 'undefined'}`, {
                defaultValue: currentOrganization?.organization.subscription_plan ?? 'Unknown',
              })}
            </p>
          </div>
          <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
            <p className="overline">{t('settings.yourRole', 'Your Role')}</p>
            <p className="text-lg font-semibold text-aurora-brand capitalize mt-1">
              {t(`roles.${currentOrganization?.role ?? 'undefined'}`, {
                defaultValue: currentOrganization?.role ?? 'Unknown',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Organization Details Form */}
      <form onSubmit={handleSubmit}>
        <div className="card p-6 space-y-6">
          <h2 className="card__title text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-aurora-brand" />
            {t('settings.details', 'Organization Details')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Organization Name */}
            <div className="md:col-span-2">
              <label className="field__label block mb-2">
                {t('settings.name', 'Organization Name')} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={!canEditSettings}
                className="input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* NIP */}
            <div>
              <label className="field__label block mb-2">{t('settings.nip', 'NIP (Tax ID)')}</label>
              <input
                type="text"
                name="nip"
                value={formData.nip}
                onChange={handleChange}
                disabled={!canEditSettings}
                placeholder="1234567890"
                className="input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Email */}
            <div>
              <label className="field__label mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t('settings.email', 'Email')}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!canEditSettings}
                placeholder="contact@company.com"
                className="input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="field__label mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('settings.phone', 'Phone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!canEditSettings}
                placeholder="+48 123 456 789"
                className="input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Website */}
            <div>
              <label className="field__label mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {t('settings.website', 'Website')}
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                disabled={!canEditSettings}
                placeholder="https://company.com"
                className="input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="pt-4 border-t border-aurora-border">
            <h3 className="overline mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-aurora-brand" />
              {t('settings.address', 'Address')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Street Address */}
              <div className="md:col-span-2">
                <label className="field__label block mb-2">
                  {t('settings.streetAddress', 'Street Address')}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={!canEditSettings}
                  placeholder="ul. Przykładowa 123"
                  className="input disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* City */}
              <div>
                <label className="field__label block mb-2">{t('settings.city', 'City')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!canEditSettings}
                  placeholder="Warszawa"
                  className="input disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Postal Code */}
              <div>
                <label className="field__label block mb-2">
                  {t('settings.postalCode', 'Postal Code')}
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  disabled={!canEditSettings}
                  placeholder="00-001"
                  className="input disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Country */}
              <div>
                <label className="field__label block mb-2">
                  {t('settings.country', 'Country')}
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={!canEditSettings}
                  placeholder="Polska"
                  className="input disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Strefa czasowa — zegar organizacji.
              To nie jest kosmetyka wyświetlania: z tej wartości NEXUS bierze
              „teraz" (blok ZEGAR w promptcie), generator agendy liczy dobę
              roboczą, a dziennik z kamer — porę zdarzeń. Zła strefa = zła ocena
              terminów i opóźnień. */}
          <div className="pt-4 border-t border-gray-700">
            <h3 className="text-md font-medium text-aurora-text mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              {t('settings.timezone', 'Strefa czasowa')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="org-timezone"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  {t('settings.timezoneLabel', 'Strefa czasowa organizacji')}
                </label>
                <select
                  id="org-timezone"
                  name="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  disabled={!canEditSettings}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-ids-md text-aurora-text focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {strefy.map((strefa) => (
                    <option key={strefa} value={strefa}>
                      {strefa}
                    </option>
                  ))}
                </select>
                {canEditSettings && strefaLokalna !== timezone && (
                  <button
                    type="button"
                    onClick={() => setTimezone(strefaLokalna)}
                    className="mt-2 text-sm text-blue-400 hover:text-blue-300"
                  >
                    {t('settings.timezoneUseLocal', 'Ustaw jak na tym urządzeniu')} ({strefaLokalna}
                    )
                  </button>
                )}
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-sm text-gray-400">
                  {t('settings.timezoneNow', 'W tej strefie jest teraz')}:
                </p>
                <p className="text-aurora-text font-medium">{podgladCzasu(timezone)}</p>
                <p className="mt-2 text-xs text-gray-500">
                  {t(
                    'settings.timezoneHint',
                    'Z tej strefy NEXUS bierze aktualną datę i godzinę — na niej opiera ocenę terminów, opóźnień i postępu prac.'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          {canEditSettings && (
            <div className="flex justify-end pt-4">
              <button type="submit" disabled={isSaving} className="btn btn--primary">
                <Save className="w-5 h-5" />
                {isSaving
                  ? t('common.saving', 'Saving...')
                  : t('common.saveChanges', 'Save Changes')}
              </button>
            </div>
          )}
        </div>
      </form>

      {/* Danger Zone */}
      {hasRole('owner') && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-ids-xl p-6">
          <h2 className="display text-lg font-semibold text-red-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {t('settings.dangerZone', 'Danger Zone')}
          </h2>
          <p className="text-aurora-muted mb-4">
            {t(
              'settings.deleteWarning',
              'Once you delete your organization, there is no going back. Please be certain.'
            )}
          </p>
          <button onClick={() => setShowDeleteConfirm(true)} className="btn btn--danger">
            <Trash2 className="w-5 h-5" />
            {t('settings.deleteOrganization', 'Delete Organization')}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-aurora-surface rounded-ids-xl border border-red-500/30 shadow-elev-4 p-6 w-full max-w-xl">
            <h3 className="display text-lg font-semibold text-aurora-text mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {t('settings.confirmDelete', 'Confirm Deletion')}
            </h3>
            <p className="text-aurora-muted mb-6">
              {t(
                'settings.confirmDeleteText',
                'Are you sure you want to delete this organization? This action cannot be undone and all data will be permanently lost.'
              )}
            </p>
            {/* Samoobsługowe usuwanie organizacji nie jest wdrożone — wcześniej
                ten przycisk cicho nic nie robił, udając destrukcyjną akcję. */}
            <p className="text-sm text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-ids-md px-3 py-2 mb-6">
              {t(
                'settings.deleteViaSupport',
                'Usuwanie organizacji wymaga kontaktu z pomocą techniczną — zgłoś wniosek w sekcji Wsparcie.'
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn--secondary flex-1"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                disabled
                title={t('settings.deleteViaSupport', 'Skontaktuj się ze wsparciem')}
                className="btn btn--danger flex-1 cursor-not-allowed"
              >
                {t('settings.deleteOrganization', 'Delete Organization')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
